import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { ReactSelectInput } from '@hivebrite/design-system/Inputs';
import { Label } from '@hivebrite/design-system/Label';

import { NETWORK_FEED_TYPE } from 'hb-react/frontoffice/apps/LiveFeed/constants/FeedType';
import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { useProjectsCustomName } from 'hb-react/frontoffice/apps/VenturesV2/hooks/useProjectsCustomName';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const Filters = ({ onChange, value, width }) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const outboxedFontTheme = useOutboxedFontTheme();
  const { config } = useLiveFeedContext();
  const { FEED_SEARCH_ITEM_LABEL } = useProjectsCustomName();
  const showJobs =
    currentNetwork.network_config.livefeed_opportunities_feed_enabled;
  const showProjects =
    currentNetwork.network_config.livefeed_ventures_feed_enabled;
  const showSurveys =
    currentNetwork.network_config.livefeed_surveys_feed_enabled;

  const filterItemsNetwork = useMemo(
    () =>
      [
        {
          label: __('All'),
          value: {},
        },
        currentNetwork?.network_configuration?.news?.published && {
          label: __('Communication'),
          value: {
            types: ['communications_post_communication'],
          },
        },
        {
          label: __('Posts'),
          value: {
            types: ['posts_feed_post'],
          },
        },
        currentNetwork?.network_configuration?.events?.published && {
          label: __('Events'),
          value: { types: ['event', 'network_events_event'] },
        },
        showJobs &&
          currentNetwork?.network_configuration?.business_opportunities
            ?.published && {
            label: I18n.t('web.feed.business'),
            value: {
              types: [
                'business_opportunities_admin_request',
                'business_opportunities_external_provider_request',
                'business_opportunities_external_recruiter_request',
                'business_opportunities_user_request',
              ],
            },
          },
        showProjects &&
          currentNetwork?.network_configuration?.ventures?.published && {
            label: FEED_SEARCH_ITEM_LABEL,
            value: {
              types: ['ventures_venture'],
            },
          },
        {
          label: I18n.t('web.feed.trips'),
          value: { types: ['location'] },
        },
        showSurveys &&
          currentNetwork?.network_configuration?.surveys?.published && {
            label: I18n.t('web.feed.surveys'),
            value: {
              types: ['surveys_form'],
            },
          },
        {
          label: I18n.t('web.feed.my_publications'),
          value: { query: { user_id: [currentUser.id] } },
        },
      ].filter(Boolean),
    [currentNetwork, FEED_SEARCH_ITEM_LABEL],
  );

  const filterItemsTopic = useMemo(
    () => [
      {
        label: __('All'),
        value: {},
      },
      ...(config?.topic?.tags || []).map((item) => ({
        label: item.name,
        value: { tag_ids: [item.id] },
      })),
    ],
    [config?.topic?.tags],
  );

  const currentFilter = useMemo(
    () =>
      config.type === NETWORK_FEED_TYPE
        ? filterItemsNetwork.find((item) => item === value) ||
          filterItemsNetwork[0]
        : filterItemsTopic.find((item) => item === value) ||
          filterItemsTopic[0],
    [filterItemsNetwork, filterItemsTopic, value, config.type],
  );

  return (
    <Box
      alignItems="center"
      justifyContent="flex-end"
      flex="1 1 auto"
      mx={{ _: 4, m: 0 }}
    >
      <ThemeProvider
        theme="fo"
        fontBrightness={outboxedFontTheme.fontBrightness}
      >
        <Label position="left" id="livefeed-filter-input">
          {__('Filter by:')}
        </Label>
      </ThemeProvider>
      <Box display="block">
        <ReactSelectInput
          ariaLabel={__('Filter by:')}
          id="livefeed-filter"
          name="livefeed-filter"
          options={
            config.type === NETWORK_FEED_TYPE
              ? filterItemsNetwork
              : filterItemsTopic
          }
          width={width}
          onChange={onChange}
          value={currentFilter}
          variant="small"
          isSearchable={false}
        />
      </Box>
    </Box>
  );
};

Filters.defaultProps = {
  width: '152px',
};

export default Filters;
